/**
 * Data hooks for BESC Hyperchain — REST API from MoneyX Indexer
 * All hooks return [data, loading, error] to match existing hook API.
 */

import { useState, useEffect, useMemo } from 'react'

// Server-side SSR: use absolute URL to reach indexer directly.
// Client-side browser: use relative URL so requests proxy through Razzle (same-origin, no CSP issue).
const API_BASE = typeof window === 'undefined'
  ? (process.env.REACT_APP_INDEXER_URL || 'https://api.moneyxpro.com')
  : ''

const MOVING_AVERAGE_DAYS   = 7
const MOVING_AVERAGE_PERIOD = 86400 * MOVING_AVERAGE_DAYS

function useIndexerFetch(path) {
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    fetch(`${API_BASE}${path}`)
      .then(r => r.json())
      .then(d => { if (!cancelled) { setData(d); setLoading(false) } })
      .catch(e => { if (!cancelled) { setError(e); setLoading(false) } })
    return () => { cancelled = true }
  }, [path])

  return [data, loading, error]
}

// ── Volume ────────────────────────────────────────────────────────────────────

export function useVolumeDataBesc({ from, to } = {}) {
  const fromTs = from || Math.floor(Date.now() / 1000) - 90 * 86400
  const [raw, loading, error] = useIndexerFetch(`/api/stats/volume?from=${fromTs}&period=day`)

  const data = useMemo(() => {
    if (!raw || !raw.daily) return null

    const sorted = [...raw.daily].sort((a, b) => a.day_ts - b.day_ts)
    let cumulative = 0
    const cumulativeByTs = {}

    return sorted.map(item => {
      const volumeUsd = Number(item.volume_usd) / 1e30
      cumulative += volumeUsd
      cumulativeByTs[item.day_ts] = cumulative

      let movingAverageAll
      const movingAverageTs = item.day_ts - MOVING_AVERAGE_PERIOD
      if (movingAverageTs in cumulativeByTs) {
        movingAverageAll = (cumulative - cumulativeByTs[movingAverageTs]) / MOVING_AVERAGE_DAYS
      }

      return {
        timestamp: item.day_ts,
        all: volumeUsd,
        swap: 0,
        margin: volumeUsd,
        liquidation: 0,
        mint: 0,
        burn: 0,
        cumulative,
        movingAverageAll,
      }
    })
  }, [raw])

  return [data, loading, error]
}

// ── Fees ──────────────────────────────────────────────────────────────────────

export function useFeesDataBesc({ from, to } = {}) {
  const fromTs = from || Math.floor(Date.now() / 1000) - 90 * 86400
  const [raw, loading, error] = useIndexerFetch(`/api/stats/fees?from=${fromTs}`)

  const data = useMemo(() => {
    if (!raw || !raw.daily) return null

    const sorted = [...raw.daily].sort((a, b) => a.day_ts - b.day_ts)
    let cumulative = 0

    return sorted.map(item => {
      const swap   = Number(item.swap   || 0) / 1e30
      const margin = Number(item.margin || 0) / 1e30
      const total  = Number(item.total  || 0) / 1e30 || swap + margin
      cumulative += total

      return {
        timestamp: item.day_ts,
        swap,
        margin,
        liquidation: 0,
        mint: 0,
        burn: 0,
        all: total,
        cumulative,
      }
    })
  }, [raw])

  return [data, loading, error]
}

// ── Users (daily breakdown) ───────────────────────────────────────────────────

export function useUsersDataBesc({ from, to } = {}) {
  const fromTs = from || Math.floor(Date.now() / 1000) - 90 * 86400
  const [raw, loading, error] = useIndexerFetch(`/api/stats/users?from=${fromTs}`)

  const data = useMemo(() => {
    if (!raw) return null

    // Merge dailyNew and dailyActive into a chart-friendly format
    const newByDay    = {}
    const activeByDay = {}
    ;(raw.dailyNew    || []).forEach(r => { newByDay[r.day_ts]    = r.new_count })
    ;(raw.dailyActive || []).forEach(r => { activeByDay[r.day_ts] = r.active_count })

    const allDays = Array.from(
      new Set([...Object.keys(newByDay), ...Object.keys(activeByDay)].map(Number))
    ).sort((a, b) => a - b)

    let cumulative = 0
    return allDays.map(day_ts => {
      const newCount = newByDay[day_ts] || 0
      cumulative += newCount
      return {
        timestamp: day_ts,
        newCount,
        uniqueCount: activeByDay[day_ts] || 0,
        uniqueCountCumulative: raw.totalUsers || cumulative,
      }
    })
  }, [raw])

  return [data, loading, error]
}

// ── Traders P&L + Open Interest over time ────────────────────────────────────

export function useTradersDataBesc({ from, to } = {}) {
  const fromTs = from || Math.floor(Date.now() / 1000) - 90 * 86400
  const [raw, loading, error] = useIndexerFetch(`/api/stats/traders?from=${fromTs}`)
  return [raw || null, loading, error]
}

// ── Referrals ─────────────────────────────────────────────────────────────────

export function useReferralsDataBesc({ from, to } = {}) {
  const fromTs = from || Math.floor(Date.now() / 1000) - 90 * 86400
  const [raw, loading, error] = useIndexerFetch(`/api/stats/referrals?from=${fromTs}`)
  const data = useMemo(() => raw?.data || null, [raw])
  return [data, loading, error]
}

// ── Pool composition history ──────────────────────────────────────────────────

export function usePoolDataBesc({ from, to } = {}) {
  const fromTs = from || Math.floor(Date.now() / 1000) - 90 * 86400
  const [raw, loading, error] = useIndexerFetch(`/api/stats/pool?from=${fromTs}`)
  return [raw || null, loading, error]
}

// ── Funding rates (hourly snapshots) ─────────────────────────────────────────

export function useFundingRatesDataBesc({ from } = {}) {
  const fromTs = from || Math.floor(Date.now() / 1000) - 30 * 86400
  const [raw, loading, error] = useIndexerFetch(`/api/stats/funding-rates?from=${fromTs}`)
  return [raw?.data || null, loading, error]
}

// ── Liquidations history ──────────────────────────────────────────────────────

export function useLiquidationsDataBesc({ from } = {}) {
  const fromTs = from || Math.floor(Date.now() / 1000) - 90 * 86400
  const [raw, loading, error] = useIndexerFetch(`/api/stats/liquidations?from=${fromTs}`)
  return [raw?.data || null, loading, error]
}

// Token address → symbol map for BESC markets
const BESC_SYMBOLS = {
  '0x7130d2a12b9bcbfae4f2634d864a1ee1ce3ead9c': 'BTC',
  '0x2170ed0880ac9a755fd29b2688956bd959f933f8': 'ETH',
  '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c': 'BNB',
  '0x570a5d26f7765ecb712c0924e4de545b89fd43df': 'SOL',
  '0x1d2f0da169ceb9fc7b3144628db156f3f6c60dbe': 'XRP',
  '0xba2ae424d960c26247dd6c32edc70b295c744c43': 'DOGE',
  '0x3ee2200efb3400fabb9aacf31297cbdd1d435d47': 'ADA',
  '0x85eac5ac2f758618dfa09bdbe0cf174e7d574d5b': 'TRX',
  '0xf8a0bf9cf54bb92f17374d9e9a321e6a111a51bd': 'LINK',
  '0x1ce0c2827e2ef14d5c4f29a091d735a204794041': 'AVAX',
  '0x2859e4544c4bb03966803b044a93563bd2d0dd4d': 'SHIB',
  '0x76a797a59ba2c17726896976b7b3747bfd1d220f': 'TON',
  '0x7083609fce4d1d8dc0c979aab8c869ea2c873402': 'DOT',
  '0x1fa4a73a3f0133f0025378af00236f3abdee5d63': 'NEAR',
  '0xcc42724c6683b7e57334c4e856f4c9965ed682bd': 'MATIC',
  '0x4338665cbb7b2485a8855a139b75d5e34ab0db94': 'LTC',
  '0xbf5140a22578168fd562dccf235e5d43a02ce9b1': 'UNI',
  '0x0eb3a705fc54725037cc9e008bdede697f62f335': 'ATOM',
  '0xa050ffb3eeb8200eeb7f61ce34ff644420fd3522': 'ARB',
  '0x5c1e3a76aba3c84afbce7a38e8ef9dea3f9adfc8': 'OP',
  '0x53544f434b000000000000000000000000000001': 'AAPL',
  '0x53544f434b000000000000000000000000000002': 'TSLA',
  '0x53544f434b000000000000000000000000000003': 'NVDA',
  '0x53544f434b000000000000000000000000000004': 'MSFT',
  '0x53544f434b000000000000000000000000000005': 'GOOGL',
  '0x53544f434b000000000000000000000000000006': 'AMZN',
  '0x53544f434b000000000000000000000000000007': 'META',
  '0x53544f434b000000000000000000000000000008': 'SPY',
  '0x53544f434b000000000000000000000000000009': 'QQQ',
  '0x53544f434b00000000000000000000000000000a': 'GOLD',
  '0x33e22f85cc1877697773ca5c85988663388883a0': 'WBESC',
  '0xf794e0b0c0a91099ccacac296193e04fb860442a': 'MONEY',
}

function resolveSymbol(address) {
  return BESC_SYMBOLS[address.toLowerCase()] || address.slice(0, 8)
}

// ── Open Interest by market (current snapshot) ───────────────────────────────

export function useOiByMarketBesc() {
  const [raw, loading, error] = useIndexerFetch('/api/stats/markets')
  const data = useMemo(() => {
    if (!raw) return null
    return Object.entries(raw)
      .map(([address, m]) => ({
        market:   resolveSymbol(address),
        longOI:   Number(m.longOpenInterest  || 0) / 1e30,
        shortOI:  Number(m.shortOpenInterest || 0) / 1e30,
        totalOI:  (Number(m.longOpenInterest || 0) + Number(m.shortOpenInterest || 0)) / 1e30,
      }))
      .filter(m => m.totalOI > 0)
      .sort((a, b) => b.totalOI - a.totalOI)
  }, [raw])
  return [data, loading, error]
}

// ── Totals (summary cards) ────────────────────────────────────────────────────

export function useBescSummary() {
  const [fees,    feesLoading]    = useIndexerFetch('/api/stats/fees')
  const [vol,     volLoading]     = useIndexerFetch('/api/stats/volume')
  const [users,   usersLoading]   = useIndexerFetch('/api/stats/users')
  const [markets, marketsLoading] = useIndexerFetch('/api/stats/markets')
  const [pool,    poolLoading]    = useIndexerFetch('/api/stats/pool')
  const [tokens,  tokensLoading]  = useIndexerFetch('/api/stats/tokens')

  const loading = feesLoading || volLoading || usersLoading || marketsLoading || poolLoading || tokensLoading

  const summary = useMemo(() => {
    let totalLong = 0, totalShort = 0
    if (markets) {
      Object.values(markets).forEach(m => {
        totalLong  += Number(m.longOpenInterest  || 0) / 1e30
        totalShort += Number(m.shortOpenInterest || 0) / 1e30
      })
    }

    // Latest AUM from pool snapshots
    let totalAum = 0, prevAum = 0
    if (pool && pool.data && pool.data.length > 0) {
      totalAum = pool.data[pool.data.length - 1].all || 0
      if (pool.data.length > 1) prevAum = pool.data[pool.data.length - 2].all || 0
    }

    return {
      totalFees:    fees   ? (Number(fees.totalFees)   / 1e30) : 0,
      weeklyFees:   fees   ? (Number(fees.weeklyFees)  / 1e30) : 0,
      totalVolume:  vol    ? (Number(vol.totalVolume)  / 1e30) : 0,
      dailyVolume:  vol    ? (Number(vol.dailyVolume)  / 1e30) : 0,
      totalUsers:   users  ? users.totalUsers    : 0,
      activeUsers:  users  ? users.activeUsers30d : 0,
      longOI:       totalLong,
      shortOI:      totalShort,
      totalAum,
      aumDelta: totalAum - prevAum,

      // Live chain stats (TVL / MONEY / MLP) — /api/stats/tokens
      tvl:             tokens ? tokens.tvl                 : 0,
      moneyPrice:      tokens ? tokens.money.price         : 0,
      moneySupply:     tokens ? tokens.money.supply        : 0,
      moneyStaked:     tokens ? tokens.money.staked        : 0,
      moneyStakedUsd:  tokens ? tokens.money.stakedUsd     : 0,
      moneyMarketCap:  tokens ? tokens.money.marketCap     : 0,
      mlpPrice:        tokens ? tokens.mlp.price           : 0,
      mlpSupply:       tokens ? tokens.mlp.supply          : 0,
    }
  }, [fees, vol, users, markets, pool, tokens])

  return [summary, loading]
}
